import { DrawShapeSource, DrawShapeData } from "helpers/models";
import gcoord from "gcoord";
import { isOutOfChina } from "helpers/coordinate-transform";

export interface LineAnalysisParams {
  catNetLayers: string[];
  nodes: { longitude: number; latitude: number }[];
  mapLevel?: number;
  spatial?: string;
  shapeId?: string;
  isReAnalysis?: boolean;
}

export interface LineValidateParams {
  catNetLayers: string[];
  shapeId?: string;
  spatial: string;
  responseStructure: any[];
  climateRiskScores: any[];
  isReAnalysis?: boolean;
}

const resolveOptionalShapeId = (selectedPolyline: DrawShapeData): string | undefined => {
  const isMyShapeRef =
    selectedPolyline.source === DrawShapeSource.SHAPE_LINE ||
    selectedPolyline.source === DrawShapeSource.SHAPE_POLYGON;

  if (!isMyShapeRef) {
    return undefined;
  }

  return selectedPolyline.shapeId || undefined;
};

const getLngLat = (point: any): [number, number] | undefined => {
  if (!point) {
    return undefined;
  }

  if (Array.isArray(point) && point.length >= 2) {
    const [lng, lat] = point;
    if (typeof lng === "number" && typeof lat === "number") {
      return [lng, lat];
    }
  }

  if (typeof point.lng === "number" && typeof point.lat === "number") {
    return [point.lng, point.lat];
  }

  if (typeof point.getLng === "function" && typeof point.getLat === "function") {
    const lng = point.getLng();
    const lat = point.getLat();
    if (typeof lng === "number" && typeof lat === "number") {
      return [lng, lat];
    }
  }

  return undefined;
};

const normalizeMapPath = (path: any): [number, number][] => {
  if (!Array.isArray(path) || !path.length) {
    return [];
  }

  const candidatePath = Array.isArray(path[0]) ? path[0] : path;
  return candidatePath
    .map((point: any) => getLngLat(point))
    .filter((point): point is [number, number] => Array.isArray(point));
};

const getSpatialByPath = (path: any): "GCJ02" | "WGS84" => {
  const normalizedPath = normalizeMapPath(path);
  if (normalizedPath.length < 2) {
    return "WGS84";
  }

  const allDomestic = normalizedPath.every(
    ([lng, lat]) => !isOutOfChina(lng, lat)
  );
  return allDomestic ? "GCJ02" : "WGS84";
};

const getPathNodes = (path: any) =>
  normalizeMapPath(path).map(([longitude, latitude]) => ({
    longitude,
    latitude
  }));

const isValidCoordinate = (coord: any): coord is [number, number] =>
  Array.isArray(coord) &&
  coord.length >= 2 &&
  typeof coord[0] === "number" &&
  typeof coord[1] === "number";

const toNodes = (coordinates: [number, number][]) =>
  coordinates.map((coord) => ({
    longitude: coord[0],
    latitude: coord[1]
  }));

/**
 * SHAPE_LINE 坐标处理流程：
 * 1) 读取 sourceSpatial 与 rawCoordinates。
 * 2) 按需生成 wgs84ForJudge（GCJ02 -> WGS84；WGS84 直接复用）。
 * 3) 用 wgs84ForJudge 全量执行 isOutOfChina。
 * 4) 若全部点在国内：outputSpatial = GCJ02。
 * 5) 否则（全境外或跨境）：outputSpatial = WGS84。
 * 6) 根据 outputSpatial 生成 outputCoordinates，最终保证 nodes 与 spatial 一致。
 */
export const buildLineAnalysisParams = (
  selectedPolyline: DrawShapeData | undefined,
  map: any,
  layers: string[],
  isReAnalysis = false
): LineAnalysisParams | undefined => {
  if (!selectedPolyline?.aMapRef) {
    return undefined;
  }

  const mapLevel = selectedPolyline.aMapRef.zoom || Math.floor(map.getZoom());

  const shapeId = resolveOptionalShapeId(selectedPolyline);

  if (selectedPolyline.source === DrawShapeSource.SHAPE_LINE) {
    const sourceSpatial = selectedPolyline.spatial || "GCJ02";
    const rawCoordinates = (selectedPolyline.coordinates || []).filter(
      isValidCoordinate
    );

    // 先统一到 WGS84，仅用于 isOutOfChina 的境内外判定。
    let wgs84ForJudge: [number, number][] = rawCoordinates;
    if (sourceSpatial === "GCJ02") {
      wgs84ForJudge = rawCoordinates.map((coord) =>
        gcoord.transform(coord, gcoord.GCJ02, gcoord.WGS84)
      );
    }

    // 只有全部点都在境内，才走 GCJ02 输出；其余（全境外/跨境）统一走 WGS84。
    const allDomestic =
      wgs84ForJudge.length > 0 &&
      wgs84ForJudge.every(([lng, lat]) => !isOutOfChina(lng, lat));

    let outputSpatial: "GCJ02" | "WGS84" = "WGS84";
    if (allDomestic) {
      outputSpatial = "GCJ02";
    }

    // 输出坐标尽量复用现有坐标，避免不必要的往返转换带来偏差。
    let outputCoordinates: [number, number][] = rawCoordinates;
    if (outputSpatial === "GCJ02") {
      if (sourceSpatial === "GCJ02") {
        outputCoordinates = rawCoordinates;
      } else {
        outputCoordinates = rawCoordinates.map((coord) =>
          gcoord.transform(coord, gcoord.WGS84, gcoord.GCJ02)
        );
      }
    } else if (sourceSpatial === "WGS84") {
      outputCoordinates = rawCoordinates;
    } else {
      outputCoordinates = wgs84ForJudge;
    }

    return {
      catNetLayers: layers,
      nodes: toNodes(outputCoordinates),
      mapLevel,
      spatial: outputSpatial,
      shapeId,
      isReAnalysis
    };
  } else {
    const path = selectedPolyline.aMapRef.getPath();
    const nodes = getPathNodes(path);
    if (nodes.length < 2) {
      return undefined;
    }

    return {
      catNetLayers: layers,
      nodes,
      mapLevel,
      spatial: getSpatialByPath(path),
      shapeId,
      isReAnalysis
    };
  }
};
export const buildLineValidateParams = (
  selectedPolyline: DrawShapeData | undefined,
  layers: string[],
  isReAnalysis = false
): LineValidateParams | undefined => {
  if (!selectedPolyline?.aMapRef) {
    return undefined;
  }

  const shapeId = resolveOptionalShapeId(selectedPolyline);

  if (selectedPolyline.source === DrawShapeSource.SHAPE_LINE) {
    return {
      catNetLayers: layers,
      responseStructure: [],
      climateRiskScores: [],
      shapeId,
      spatial: selectedPolyline.spatial || "GCJ02",
      isReAnalysis
    };
  }

  const path = selectedPolyline.aMapRef.getPath();

  return {
    catNetLayers: layers,
    responseStructure: [],
    climateRiskScores: [],
    shapeId,
    spatial: getSpatialByPath(path),
    isReAnalysis
  };
};
